import { useState } from 'react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import { Clock, CreditCard, Database, Plus, RefreshCw, Sheet, Trash2 } from 'lucide-react'
import { clsx } from 'clsx'
import { AlertBanner } from '../components/ui/AlertBanner'
import { ConnectorWizard } from '../components/connectors/ConnectorWizard'
import {
  useDeleteSource,
  useSources,
  useSyncSource,
  type DataSource,
} from '../hooks/useSources'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const SOURCE_TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  google_sheets: { label: 'Google Sheets', icon: Sheet,      color: 'text-emerald-500' },
  stripe:        { label: 'Stripe',         icon: CreditCard, color: 'text-indigo-500' },
  float:         { label: 'Float',          icon: Clock,      color: 'text-amber-500' },
}

function getSourceConfig(type: string) {
  return SOURCE_TYPE_CONFIG[type] ?? { label: type, icon: Database, color: 'text-[--km-text-muted]' }
}

function formatLastSync(ts: string | null): string {
  if (!ts) return 'Mai sincronizzata'
  return format(new Date(ts), "d MMM yyyy 'alle' HH:mm", { locale: it })
}

// ─────────────────────────────────────────────────────────────────────────────
// SourceCard
// ─────────────────────────────────────────────────────────────────────────────

interface SourceCardProps {
  source: DataSource
  onSync: (id: string) => void
  onDelete: (id: string) => void
  syncing: boolean
  deleting: boolean
}

function SourceCard({ source, onSync, onDelete, syncing, deleting }: SourceCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const { label, icon: Icon, color } = getSourceConfig(source.source_type)

  return (
    <div
      className="flex flex-col gap-4 rounded-xl border border-[--km-border] bg-[--km-surface] p-5 sm:flex-row sm:items-center"
      role="article"
      aria-label={`Sorgente ${source.name}`}
    >
      {/* Icon + info */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[--km-surface-3]">
          <Icon size={20} className={color} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-[--km-text-primary] truncate">{source.name}</p>
            <span
              className={clsx(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium',
                source.is_active
                  ? 'bg-emerald-500/10 text-emerald-400'
                  : 'bg-[--km-surface-3] text-[--km-text-muted]',
              )}
            >
              {source.is_active ? 'Attiva' : 'Inattiva'}
            </span>
          </div>
          <p className="text-xs text-[--km-text-muted] mt-0.5">{label}</p>
          <p className="text-xs text-[--km-text-muted] mt-0.5">
            Ultimo sync: {formatLastSync(source.last_sync_at)}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        {/* Sync button */}
        <button
          onClick={() => onSync(source.id)}
          disabled={syncing}
          className={clsx(
            'flex items-center gap-1.5 rounded-lg border border-[--km-border] px-3 py-1.5 text-xs font-medium',
            'text-[--km-text-secondary] transition-colors',
            syncing ? 'opacity-60 cursor-not-allowed' : 'hover:border-[--km-border-hover] hover:text-[--km-text-primary]',
          )}
          aria-label={`Sincronizza ${source.name}`}
        >
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} aria-hidden="true" />
          {syncing ? 'Sync…' : 'Sincronizza'}
        </button>

        {/* Delete */}
        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onDelete(source.id)}
              disabled={deleting}
              className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-1.5 text-xs font-medium text-[--km-error] hover:bg-red-500/20 transition-colors"
              aria-label={`Conferma eliminazione ${source.name}`}
            >
              {deleting ? 'Eliminazione…' : 'Conferma'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="rounded-lg border border-[--km-border] px-3 py-1.5 text-xs font-medium text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors"
              aria-label="Annulla eliminazione"
            >
              Annulla
            </button>
          </div>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            className="p-2 rounded-lg text-[--km-text-muted] hover:text-[--km-error] hover:bg-red-500/5 transition-colors"
            aria-label={`Elimina sorgente ${source.name}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sources page
// ─────────────────────────────────────────────────────────────────────────────

export function Sources() {
  const [showWizard, setShowWizard] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { data: sources, isLoading, error } = useSources()
  const syncMutation = useSyncSource()
  const deleteMutation = useDeleteSource()

  function handleWizardSuccess(source: DataSource) {
    setShowWizard(false)
    setSuccessMessage(`Sorgente "${source.name}" creata con successo.`)
  }

  function handleSync(id: string) {
    syncMutation.mutate(id)
  }

  function handleDelete(id: string) {
    deleteMutation.mutate(id)
  }

  // ── Wizard view ───────────────────────────────────────────────────────────
  if (showWizard) {
    return (
      <div className="max-w-2xl">
        <ConnectorWizard
          onClose={() => setShowWizard(false)}
          onSuccess={handleWizardSuccess}
        />
      </div>
    )
  }

  // ── Sources list ──────────────────────────────────────────────────────────
  return (
    <section aria-label="Sorgenti dati" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[--km-text-primary]">Sorgenti dati</h1>
          <p className="text-sm text-[--km-text-muted] mt-0.5">
            Gestisci le connessioni a Google Sheets, Stripe, Float e altri sistemi.
          </p>
        </div>
        <button
          onClick={() => {
            setSuccessMessage(null)
            setShowWizard(true)
          }}
          className="flex items-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          aria-label="Aggiungi nuova sorgente dati"
        >
          <Plus size={16} aria-hidden="true" />
          Aggiungi sorgente
        </button>
      </div>

      {/* Success banner */}
      {successMessage && (
        <AlertBanner
          severity="success"
          message={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}

      {/* Sync error */}
      {syncMutation.error && (
        <AlertBanner
          severity="critical"
          message={`Sincronizzazione fallita: ${syncMutation.error.message}`}
        />
      )}

      {/* Delete error */}
      {deleteMutation.error && (
        <AlertBanner
          severity="critical"
          message={`Impossibile eliminare la sorgente: ${deleteMutation.error.message}`}
        />
      )}

      {/* Fetch error */}
      {error && (
        <AlertBanner
          severity="critical"
          message="Impossibile caricare le sorgenti dati. Riprovare."
        />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-24 rounded-xl border border-[--km-border] bg-[--km-surface] animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && sources?.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[--km-border] bg-[--km-surface] py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[--km-surface-3] mb-4">
            <Database size={24} className="text-[--km-text-muted]" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-[--km-text-primary]">Nessuna sorgente configurata</p>
          <p className="text-xs text-[--km-text-muted] mt-1 mb-6 max-w-xs">
            Aggiungi la prima sorgente per iniziare a sincronizzare i tuoi dati.
          </p>
          <button
            onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
          >
            <Plus size={14} aria-hidden="true" />
            Aggiungi sorgente
          </button>
        </div>
      )}

      {/* Sources list */}
      {!isLoading && sources && sources.length > 0 && (
        <div className="space-y-3">
          {sources.map((source) => (
            <SourceCard
              key={source.id}
              source={source}
              onSync={handleSync}
              onDelete={handleDelete}
              syncing={syncMutation.isPending && syncMutation.variables === source.id}
              deleting={deleteMutation.isPending && deleteMutation.variables === source.id}
            />
          ))}
        </div>
      )}

      {/* Info box */}
      <div className="rounded-xl border border-[--km-border] bg-[--km-surface] p-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-[--km-text-secondary] mb-3">
          Connettori disponibili
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {Object.entries(SOURCE_TYPE_CONFIG).map(([, cfg]) => {
            const Icon = cfg.icon
            return (
              <div key={cfg.label} className="flex items-center gap-2.5">
                <Icon size={15} className={cfg.color} aria-hidden="true" />
                <span className="text-xs text-[--km-text-secondary]">{cfg.label}</span>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
